import { useEffect, useState } from 'react';
import {
  Button,
  Space,
  Table,
  Tag,
  Modal,
  Descriptions,
  Divider,
  Input,
  Popconfirm,
  message,
} from 'antd';
import Layouts from '../../../components/Layouts';
import { useSelector } from 'react-redux';

import { getDirectusClient } from '../../../lib/directus';
import { Link } from 'react-router-dom';
import { deleteItem, deleteItems, readItems } from '@directus/sdk';
import validarpermiso from '../../../lib/utils';
import { getImpuestosByGrupo } from '../../../services/impuestos';
import {
  getOrdenes,
  getOrdenesByFactura,
  getOrdenesCount,
} from '../../../services/ordenes';
import PDFViewer from '../components/PDFViewer';

const Orders = () => {
  const [data, setData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPDFModalVisible, setIsPDFModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [searchFactura, setSearchFactura] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const permisos = useSelector(state => state.permisos);
  const userRoleID = useSelector(state => state.userdata.role?.name);
  const userBodega = useSelector(state => state.userdata.bodega?.id);
  const [impuestosPorGrupo, setImpuestosPorGrupo] = useState({});

  const showOrderDetails = async record => {
    setSelectedOrder(record);
    setIsModalVisible(true);
    await loadImpuestosForGroups(record.productos);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  const handleShowPDF = () => {
    setIsPDFModalVisible(true);
  };

  const handleClosePDF = () => {
    setIsPDFModalVisible(false);
  };

  const fetchOrders = async (factura = '', page = 1, pageSize = 10) => {
    if (!validarpermiso('ordenes_decompra', 'read', permisos)) {
      setHasPermissionError(true);
      return;
    }

    try {
      let orders;
      if (factura) {
        orders = await getOrdenesByFactura(factura, page, pageSize);
      } else {
        orders = await getOrdenes(userRoleID, userBodega);
      }

      // Get total count
      const countResponse = await getOrdenesCount(userRoleID, userBodega);
      const total = parseInt(countResponse[0]?.countDistinct?.id || 0);

      const processedOrders = orders.map(order => ({
        ...order,
        bodegas:
          order.bodegas.length > 0
            ? order?.bodegas[0]?.bodegas_id?.nombre
            : null,
        user_created: order.user_created.first_name,
        estado: order.estado.nombre,
      }));

      setData(processedOrders);
      setPagination(prev => ({
        ...prev,
        total: total,
      }));
      setHasPermissionError(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (
        error.response?.status === 403 ||
        error.message?.includes('permission')
      ) {
        setHasPermissionError(true);
      }
    }
  };

  const handleSearch = value => {
    setSearchFactura(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchOrders(value, 1, pagination.pageSize);
  };

  const handleTableChange = pagination => {
    setPagination(pagination);
    fetchOrders(searchFactura, pagination.current, pagination.pageSize);
  };

  const loadImpuestosForGroups = async productos => {
    const gruposIds = [
      ...new Set(productos.map(p => p.producto_detalle?.grupo)),
    ];

    const impuestosMap = {};
    for (const grupoId of gruposIds) {
      if (grupoId) {
        try {
          const impuestos = await getImpuestosByGrupo(grupoId);
          impuestosMap[grupoId] = impuestos;
        } catch (error) {
          console.error(
            `Error cargando impuestos para grupo ${grupoId}:`,
            error
          );
        }
      }
    }
    setImpuestosPorGrupo(impuestosMap);
  };

  const calculateTotals = productos => {
    return productos.reduce(
      (acc, producto) => {
        const cantidad = producto.cantidad || 0;
        const precioSinImp = producto.producto_detalle?.preciosinimp || 0;
        const grupoId = producto.producto_detalle?.grupo;

        const impuestosDelGrupo = impuestosPorGrupo[grupoId] || [];
        const impuesto = impuestosDelGrupo[0]?.porcentaje || 0;

        const subtotalSinImp = cantidad * precioSinImp;
        const impuestoValor = (subtotalSinImp * impuesto) / 100;
        const totalConImp = subtotalSinImp + impuestoValor;

        return {
          subtotalSinImp: acc.subtotalSinImp + subtotalSinImp,
          impuestoTotal: acc.impuestoTotal + impuestoValor,
          totalConImp: acc.totalConImp + totalConImp,
        };
      },
      { subtotalSinImp: 0, impuestoTotal: 0, totalConImp: 0 }
    );
  };

  // Eliminar orden de compra y sus productos_odc relacionados
  const handleDeleteOrder = async orderId => {
    if (!validarpermiso('ordenes_decompra', 'delete', permisos)) {
      message.error('No tiene permisos para eliminar órdenes de compra.');
      return;
    }
    setIsDeleteLoading(true);
    try {
      const directus = await getDirectusClient();

      // 1. Buscar todos los productos_odc relacionados a la orden
      const productosODC = await directus.request(readItems('productos_odc', {
          filter: { orden: { _eq: orderId } },
          fields: ['id'],
        })
      );

      // 2. Eliminar todos los productos_odc relacionados
      if (productosODC && productosODC.data && productosODC.data.length > 0) {
        const idsToDelete = productosODC.data.map(p => p.id);
        // Directus permite deleteMany
        await directus.request(deleteItems('productos_odc', idsToDelete));
      }

      // 3. Eliminar la orden de compra
      await directus.request(deleteItem('ordenes_decompra', orderId));

      message.success('Orden y productos asociados eliminados correctamente.');
      setIsModalVisible(false);
      setSelectedOrder(null);
      // Refrescar la lista
      fetchOrders(searchFactura, pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('Error al eliminar la orden.');
      console.error('Error al eliminar la orden y productos_odc:', error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (userBodega) {
      fetchOrders('', pagination.current, pagination.pageSize);
    }
  }, [userBodega, permisos]);
  const columns = [
    {
      title: 'Orden',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Factura',
      dataIndex: 'numero_factura',
      key: 'numero_factura',
    },
    {
      title: 'Bodega',
      dataIndex: 'bodegas',
      key: 'bodegas',
    },
    {
      title: 'Responsable',
      dataIndex: 'user_created',
      key: 'user_created',
    },
    {
      title: 'Estado',
      key: 'estado',
      dataIndex: 'estado',
      render: estado => {
        let color = '';
        switch (estado.toLowerCase()) {
          case 'creada':
            color = 'orange';
            break;
          case 'ingreso parcial':
            color = 'gold';
            break;
          case 'ingreso total':
            color = 'green';
            break;
          default:
            color = 'default';
        }
        return <Tag color={color}>{estado}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => showOrderDetails(record)}>
            Detalles
          </Button>
          {validarpermiso('ordenes_decompra', 'delete', permisos) &&
            record.estado.toLowerCase() === 'creada' && (
              <Popconfirm
                title="¿Está seguro de eliminar esta orden? Se eliminarán también los productos asociados."
                onConfirm={() => handleDeleteOrder(record.id)}
                okText="Sí"
                cancelText="No"
                okButtonProps={{ loading: isDeleteLoading }}
              >
                <Button danger loading={isDeleteLoading}>
                  Eliminar
                </Button>
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];
  return (
    <Layouts>
      <Space direction="vertical" style={{ display: 'flex' }}>
        <Space>
          <Button
            disabled={
              !validarpermiso('ordenes_decompra', 'create', permisos) ||
              hasPermissionError
            }
          >
            <Link
              to="/orders_create"
              style={{
                color:
                  !validarpermiso('ordenes_decompra', 'create', permisos) ||
                  hasPermissionError
                    ? 'rgba(0, 0, 0, 0.25)'
                    : undefined,
              }}
            >
              Agregar nueva orden
            </Link>
          </Button>
          <Input.Search
            placeholder="Buscar por número de factura"
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
        </Space>

        {!hasPermissionError ? (
          <Table
            columns={columns}
            dataSource={data}
            pagination={pagination}
            onChange={handleTableChange}
          />
        ) : (
          <h3>No tiene permisos para ver órdenes de compra</h3>
        )}
      </Space>

      <Modal
        title="Detalles de la Orden"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button
            key="pdf"
            type="primary"
            onClick={handleShowPDF}
            disabled={!selectedOrder?.comprobante}
          >
            Ver Comprobante
          </Button>,
          validarpermiso('ordenes_decompra', 'delete', permisos) &&
            selectedOrder &&
            selectedOrder.estado.toLowerCase() === 'creada' && (
              <Popconfirm
                key="delete"
                title="¿Está seguro de eliminar esta orden? Se eliminarán también los productos asociados."
                onConfirm={() => handleDeleteOrder(selectedOrder.id)}
                okText="Sí"
                cancelText="No"
                okButtonProps={{ loading: isDeleteLoading }}
              >
                <Button danger loading={isDeleteLoading}>
                  Eliminar
                </Button>
              </Popconfirm>
            ),
          <Button key="close" onClick={handleCancel}>
            Cerrar
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered>
              <Descriptions.Item label="Número de Factura" span={3}>
                {selectedOrder.numero_factura}
              </Descriptions.Item>
              <Descriptions.Item label="Estado" span={3}>
                {selectedOrder.estado}
              </Descriptions.Item>
              <Descriptions.Item label="Bodega" span={3}>
                {selectedOrder.bodegas}
              </Descriptions.Item>
              <Descriptions.Item label="Proveedor" span={3}>
                {selectedOrder.proveedor?.nombre} (
                {selectedOrder.proveedor?.documento})
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Productos</Divider>
            <Table
              dataSource={selectedOrder.productos}
              columns={[
                {
                  title: 'Producto',
                  dataIndex: ['producto_detalle', 'nombre'],
                  key: 'nombre',
                },
                {
                  title: 'Cantidad',
                  dataIndex: 'cantidad',
                  key: 'cantidad',
                },

                {
                  title: 'Precio compra',
                  dataIndex: 'precio_uni_final',
                  key: 'precio_uni_final',
                },

                {
                  title: 'ITBMS',
                  key: 'totalWithITBMS',
                  render: (_, record) => {
                    const precioFinal =
                      (record.cantidad || 0) * (record.precio_uni_final || 0);
                    const itbmsValor = precioFinal * 0.07; // 7% ITBMS
                    return itbmsValor.toFixed(2);
                  },
                },
                {
                  title: 'ISC',
                  key: 'totalWithISC',
                  render: (_, record) => {
                    const precioFinal =
                      (record.cantidad || 0) * (record.precio_uni_final || 0);

                    // Extraer los impuestos del grupo del producto
                    const impuestos =
                      record.producto_detalle?.grupo?.impuestos || [];

                    // Buscar el impuesto ISC dentro de los impuestos del grupo
                    const iscImpuesto = impuestos.find(
                      imp =>
                        imp.impuestos_id &&
                        imp.impuestos_id.nombre &&
                        imp.impuestos_id.nombre.toUpperCase() === 'ISC'
                    );

                    // Obtener el porcentaje del ISC si existe
                    const iscPorcentaje = iscImpuesto
                      ? parseFloat(iscImpuesto.impuestos_id.porcentaje) || 0
                      : 0;

                    const iscValor = (precioFinal * iscPorcentaje) / 100;
                    return iscValor.toFixed(2);
                  },
                },
                {
                  title: 'Subtotal',
                  key: 'subtotal',
                  render: (_, record) => {
                    const precioFinal =
                      (record.cantidad || 0) * (record.precio_uni_final || 0);
                    return precioFinal.toFixed(2);
                  },
                },
                {
                  title: 'Total final',
                  key: 'total_final',
                  render: (_, record) => {
                    const precioFinal =
                      (record.cantidad || 0) * (record.precio_uni_final || 0);
                    const itbmsValor = precioFinal * 0.07;
                    const grupoId = record.producto_detalle?.grupo;
                    const impuestosDelGrupo = impuestosPorGrupo[grupoId] || [];
                    const impuestoGrupo =
                      parseFloat(impuestosDelGrupo[0]?.porcentaje) || 0;
                    const iscValor = (precioFinal * impuestoGrupo) / 100;
                    const totalFinal = precioFinal + itbmsValor + iscValor;
                    return totalFinal.toFixed(2);
                  },
                },
              ]}
              pagination={false}
            />
          </div>
        )}
      </Modal>

      <PDFViewer
        visible={isPDFModalVisible}
        onClose={handleClosePDF}
        comprobante={selectedOrder?.comprobante}
      />
    </Layouts>
  );
};
export default Orders;
